import { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { TransactionDetails } from '../../../types/api.types';
import './InstallmentOptions.scss';
import { CardElement } from '../CardElement/CardElement';
import { InstallmentCards } from './InstallmentCards/InstallmentCards';
import {
  clearInstallmentData,
  restoreOriginalAmount,
} from '../../../redux/paymentActions';
import { updateTransactionModeAPI } from '../../../services/installment.service';
import { Cybersource } from '../../Cybersource/Cybersource';
import { AmexOld } from '../../AmexOld/AmexOld';
import master from '../../../assets/master_logo.png';
import visa from '../../../assets/visa_logo.png';
import amex from '../../../assets/express_logo.png';

export interface CardOptions {
  details: TransactionDetails;
  response: any;
  onStepChange?: (step: string, data?: any) => void;
  onBackNavigation?: () => boolean; // Returns true if navigation was handled
}

export const InstallmentOptions = ({
  details,
  response,
  onStepChange,
  onBackNavigation,
}: CardOptions) => {
  const [paymentType, setPaymentType] = useState(''); // 'oneoff' or 'installment'
  const [selectedBank, setSelectedBank] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [providerKey, setProviderKey] = useState('');
  const [planPeriod, setPlanPeriod] = useState(0);
  const [cardType, setCardType] = useState(''); // For CyberSource/Amex card selection
  const dispatch = useDispatch();
  const isInitialMount = useRef(true);

  // Helper function to check if a provider key exists in both old and new formats
  const hasProviderKey = (keyToCheck: string): boolean => {
    if (
      !details.data.provider_keys ||
      !Array.isArray(details.data.provider_keys)
    ) {
      return false;
    }

    // Handle new format (array of objects)
    if (
      details.data.provider_keys.length > 0 &&
      typeof details.data.provider_keys[0] === 'object'
    ) {
      const providerKeys = details.data.provider_keys as Array<{
        id: number;
        key: string;
      }>;
      return providerKeys.some((provider) => provider.key === keyToCheck);
    }

    // Handle old format (array of strings)
    const providerKeys = details.data.provider_keys as Array<string>;
    return providerKeys.includes(keyToCheck);
  };

  // Notify parent about current step whenever state changes
  useEffect(() => {
    // Skip the first render to avoid calling onStepChange with initial empty values
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (onStepChange) {
      if (selectedPlan) {
        onStepChange('plan-selected', {
          paymentType,
          selectedBank,
          selectedPlan,
          providerKey,
          period: planPeriod,
        });
      } else if (selectedBank) {
        onStepChange('bank-selected', { paymentType, selectedBank });
      } else if (paymentType) {
        onStepChange('payment-type-selected', { paymentType });
      } else {
        onStepChange('root', {});
      }
    }
  }, [
    paymentType,
    selectedBank,
    selectedPlan,
    providerKey,
    planPeriod,
    onStepChange,
  ]);

  // Handle back navigation from parent
  useEffect(() => {
    if (onBackNavigation) {
      const handleBack = () => {
        if (selectedPlan) {
          setSelectedPlan('');
          setProviderKey('');
          setPlanPeriod(0);
          return true;
        } else if (selectedBank) {
          setSelectedBank('');
          return true;
        } else if (paymentType) {
          setPaymentType('');
          // When going back to payment type selection, restore original amount
          dispatch(clearInstallmentData());
          dispatch(restoreOriginalAmount());
          return true;
        }
        return false;
      };

      // Store the handler so parent can call it
      (window as any).cardPaymentBackHandler = handleBack;
    }
  }, [selectedPlan, selectedBank, paymentType, onBackNavigation]);

  const optionSelection = async (selectedType: string) => {
    console.log('Payment option selected:', selectedType); // Debug log
    setPaymentType(selectedType);

    // Call API to update transaction mode
    try {
      const isInstallment = selectedType === 'installment';
      await updateTransactionModeAPI(
        details.data.onepay_transaction_id,
        isInstallment
      );
      console.log(
        `Transaction mode updated to: ${
          isInstallment ? 'installment' : 'one-off'
        }`
      );
    } catch (error) {
      console.error('Error updating transaction mode:', error);
    }

    // Clear installment data and restore original amount when "One Off" is selected
    if (selectedType === 'oneoff') {
      dispatch(clearInstallmentData());
      dispatch(restoreOriginalAmount());
    }
  };

  const handleCardTypeSelection = (type: string) => {
    console.log('Card type selected:', type); // Debug log
    setCardType(type);
  };

  const handleBankSelection = (bankId: string) => {
    console.log('Bank selected:', bankId); // Debug log
    setSelectedBank(bankId);
  };

  const handlePlanSelection = (
    planId: string,
    provider: string,
    period?: number
  ) => {
    console.log('Plan selected:', planId, provider, period); // Debug log
    setSelectedPlan(planId);
    setProviderKey(provider);
    setPlanPeriod(period || 0);
  };

  return (
    <div>
      {/* Root Level - Payment Type Selection */}
      {!paymentType && (
        <>
          <div
            onClick={() => optionSelection('oneoff')}
            onTouchEnd={(e) => {
              e.preventDefault();
              optionSelection('oneoff');
            }}
            className="installment-option"
          >
            One Off <span>Debit or Credit Card</span>
          </div>
          <div
            onClick={() => optionSelection('installment')}
            onTouchEnd={(e) => {
              e.preventDefault();
              optionSelection('installment');
            }}
            className="installment-option"
          >
            Installment Plan <span>Credit Cards only</span>
          </div>
        </>
      )}

      {/* One Off Payment - Non CyberSource/Amex */}
      {paymentType === 'oneoff' &&
        !hasProviderKey('CYBERSOURCE') &&
        !hasProviderKey('CYBERSOURCE-USD') &&
        !hasProviderKey('AMEX') &&
        !hasProviderKey('AMEX-USD') && <CardElement response={response} />}

      {/* One Off Payment - CyberSource/Amex Card Selection */}
      {paymentType === 'oneoff' &&
        (hasProviderKey('CYBERSOURCE') ||
          hasProviderKey('CYBERSOURCE-USD') ||
          hasProviderKey('AMEX') ||
          hasProviderKey('AMEX-USD')) &&
        !cardType && (
          <>
            <div className="row">
              <div className="col-6 ps-4 pe-4 pt-4">
                <div
                  className="card-option d-flex justify-content-center align-items-center"
                  onClick={() => handleCardTypeSelection('mpgs')}
                >
                  <img src={master} width={'37.57px'} alt="master" />
                </div>
              </div>

              <div className="col-6 ps-4 pe-4 pt-4">
                <div
                  className="card-option d-flex justify-content-center align-items-center"
                  onClick={() => handleCardTypeSelection('mpgs')}
                >
                  <img src={visa} width={'37.57px'} alt="visa" />
                </div>
              </div>
            </div>

            <div className="row d-flex justify-content-center">
              <div className="col-6 p-4">
                <div
                  className="card-option d-flex justify-content-center align-items-center"
                  onClick={() => handleCardTypeSelection('amex')}
                >
                  <img src={amex} width={'37.57px'} alt="amex" />
                </div>
              </div>
            </div>
          </>
        )}

      {/* One Off Payment - CyberSource for Visa/Master */}
      {paymentType === 'oneoff' &&
        cardType === 'mpgs' &&
        (hasProviderKey('CYBERSOURCE') ||
          hasProviderKey('CYBERSOURCE-USD')) && <Cybersource />}

      {/* One Off Payment - Amex (NTB-AMEX uses CardElement) */}
      {paymentType === 'oneoff' &&
        cardType === 'amex' &&
        (hasProviderKey('NTB-AMEX-LKR') ||
          hasProviderKey('NTB-AMEX-USD')) && <CardElement response={response} />}

      {/* One Off Payment - Amex (Old Amex flow) */}
      {paymentType === 'oneoff' &&
        cardType === 'amex' &&
        (hasProviderKey('AMEX') || hasProviderKey('AMEX-USD')) && <AmexOld />}

      {/* Installment Flow */}
      {paymentType === 'installment' && (
        <InstallmentCards
          response={response}
          details={details}
          selectedBank={selectedBank}
          selectedPlan={selectedPlan}
          providerKey={providerKey}
          onBankSelect={handleBankSelection}
          onPlanSelect={handlePlanSelection}
          cardResponse={response}
        />
      )}
    </div>
  );
};
